import type { Web3TxRoutes } from "@/hooks/tx-flow/useDepositCalculate";
import type { AxiosError } from "axios";
import type { Networkish, SupTransactionType, SupWeb3TxSingleVault } from "@/types";
import config from "@/../config/dapp.config";
import { getChainInfo } from "@/utils/formatters";
import { formatEther } from "viem";

import * as dn from "dnum";

/**
 * Deposit vault error processing
 * It uses a default error message if no calculated route is provided.
 * If bridgeRouteError prop is defined, it matches the status of each bridge with bridgeErrorMessages and uses the first match.
 *
 * Used in @/src/components/Web3/Common/VaultRouteContainer.tsx
 *
 * @param {Web3TxRoutes | undefined} calculatedRoute
 * @param {SupTransactionType} type
 * @return {Error}
 */
export function txVaultBridgeError(calculatedRoute: Web3TxRoutes | undefined, type: SupTransactionType = "deposit"): Error {
  const bridgeErrorMessages = [
    {
      status: "MIN_AMOUNT_NOT_MET",
      message: `Amount you're trying to ${type} is below bridge's minimum bridging amount for given token.`,
      actions: `Please select a DIFFERENT token, INCREASE ${type}ing amount or remove this vault.`,
    },
    {
      status: "INSUFFICIENT_INPUT_AMOUNT",
      message: "Bridge has insufficient liquidity for token on destination chain.",
      actions: "Please select a DIFFERENT token (best on DIFFERENT CHAIN) or remove this vault.",
    },
    {
      status: "MAX_AMOUNT_EXCEEDED",
      message: `Amount you're trying to ${type} exceeds bridge's maximum bridging amount for given token.`,
      actions: `Please select a DIFFERENT token, REDUCE ${type}ing amount or remove this vault.`,
    },
    {
      status: "ASSET_NOT_SUPPORTED",
      message: `Selected ${type === "deposit" ? "funding" : "target"} asset is not supported by any bridge we're using to make a cross-chain tx.`,
      actions: "Please select a DIFFERENT token, or remove this vault.",
    },
  ];

  let errorText = "We could not find a route to this vault with the selected FUNDING token. Please select a DIFFERENT token, re-calculate the route or remove this vault.";

  if (calculatedRoute?.liq?.bridgeRouteErrors) {
    bridgeErrorMessages.every(message => {
      if (calculatedRoute.liq?.bridgeRouteErrors?.some(error => error.status === message.status)) {
        errorText = `${message.message} ${message.actions}`;
        return false;
      }
      return true;
    });
  }
  return new Error(`${errorText}`);
}

/**
 * Deposit transfer error processing
 * It works for both API errors & web3 errors:
 * - for an API error it uses either error prop or first line of error message
 * - for a web3 error, it uses the error name & first line of error message
 *
 * Used in @/src/components/Web3/Deposit/Transfer.tsx
 *
 * @param {Error|null|undefined} error
 * @param chainId
 * @param vaultList
 * @return {string}
 */
export function depositTransferError(
  error: Error | null | undefined,
  chainId: Networkish | undefined = undefined,
  vaultList: SupWeb3TxSingleVault[] | undefined = undefined
): string {
  if (!error) {
    return "Unknown error";
  }

  const name = error.name;
  const [firstLine] = error.message.split("\n");
  if (name === "AxiosError") {
    return processApiError(error, undefined, vaultList);
  } else {
    if (name === "EstimateGasExecutionError" || name === "Error") {
      const beautifiedError = processEstimateGasError(error, chainId);
      if (beautifiedError) {
        return beautifiedError;
      }
    }
  }
  return `${name}: ${firstLine}`;
}

export function processEstimateGasError(error: Error, chainId: Networkish | undefined = undefined): string | undefined {
  const keyErrorMessage = error.message.split("\n").find(line => line.startsWith("Details:")) || error.message.split("\n")[0];
  if (keyErrorMessage) {
    const result = /HAVE (\d+) WANT (\d+)/gim.exec(keyErrorMessage);
    if (result) {
      const difference = dn.subtract(formatEther(BigInt(result[2])), formatEther(BigInt(result[1])), 18);
      let tokenName = "native token";
      let onChain = "";
      if (chainId) {
        const chainInfo = getChainInfo(chainId);
        if (chainInfo) {
          tokenName = chainInfo.token;
          onChain = ` on ${chainInfo.name}`;
        }
      }
      const amount =
        dn.format(difference, {
          locale: "en-US",
          digits: config.decimalsBigRoundingLimit,
        }) === "0"
          ? dn.format(difference, {
              locale: "en-US",
            })
          : dn.format(difference, {
              locale: "en-US",
              digits: config.decimalsBigRoundingLimit,
            });
      return `TX Error: You don't have enough gas in your wallet to complete the transaction. You need ${amount} more of ${tokenName}${onChain} to proceed.`;
    }

    return keyErrorMessage
      .replace(/ADDRESS \S+/gi, "")
      .replace("ERR: ", "")
      .replace("Details: ", "Gas Estimation failed: ");
  }
}

export function processApiError(error: Error | null, errorPostfix = "Please refresh or select another route.", vaultList: SupWeb3TxSingleVault[] | undefined = undefined): string {
  if (error === null) {
    return "Unknown error";
  }
  const axiosError = error as AxiosError<{
    detail?: string;
    error?: string;
    vault_id?: string;
    superform_id?: string;
    route_id?: string;
  }>;

  // Handling 504 error
  if (axiosError.response?.status === 504) {
    return `API: Service currently unavailable. ${errorPostfix}`;
  }

  if (axiosError.response !== undefined) {
    // Handling API error
    if (axiosError.response.data.detail !== undefined) {
      const result = /API ERROR: {"MESSAGE":\s*"([^"]+)"/gim.exec(axiosError.response.data.detail);
      return result ? `API: ${result[1]} ${errorPostfix}` : axiosError.response.data.detail;
    }
    // Handling no route error
    if (axiosError.response.data.error && axiosError.response.data.vault_id && axiosError.response.data.route_id && vaultList !== undefined) {
      const foundVault = vaultList.find(vault => vault.id === axiosError.response?.data.vault_id);
      if (!foundVault) {
        return `API: ${axiosError.response.data.error}. ${errorPostfix}`;
      }
      return `API: ${axiosError.response.data.error}. Please select another route for “${foundVault.friendly_name}”`;
    }
    return `API: unknown error. ${errorPostfix}`;
  }

  const [firstLine] = error.message.split("\n");
  return `${firstLine}`;
}

/**
 * Check if error is caused by user rejecting signing request
 *
 * @param {Error} error
 * @return {boolean}
 */
export function checkIfRejected(error: Error): boolean {
  return error.message.search("User rejected the request") !== -1;
}
